<template>
  <v-menu
    ref="menu"
    :value="show"
    :position-x="xPosition()"
    :position-y="yPosition()"
    @close="close()"
    absolute
    offset-y
    :close-on-content-click="false"
    width="400px"
  >
    <template v-slot:activator="{ on }">
      <div
        ref="spellingError"
        class="quill-spelling-error"
        :style="computedStyle"
        @click.prevent="showClick"
      ></div>
    </template>

    <v-card class="quill-spelling-error-card" max-width="600px">
      <v-card-title primary-title>
        <div>
          <div class="headline">{{ error.message }}</div>
        </div>
      </v-card-title>
      <v-card-text>
        <v-text-field
          ref="textInput"
          v-model="manualReplacement"
          @keyup.enter.prevent="replace(manualReplacement, false)"
          :label="$t('message.content.tools.spellcheck.spellingError.manualEdit')"
          color="secondary"
          clearable
        ></v-text-field>
        <h3 class="mt-2">{{ $t('message.content.tools.spellcheck.spellingError.replacements') }}</h3>
        <v-item-group v-if="error.replacements && error.replacements.length > 0">
          <v-item
            v-for="(replacement, i) in error.replacements.slice(0, 10)"
            :key="i"
          >
            <v-btn
              class="suggestions-button"
              round
              small
              depressed
              @click="replace(replacement.value, true)"
            >
              {{ replacement.value }}
            </v-btn>
          </v-item>
        </v-item-group>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn flat color="secondary" @click.prevent="close()">
          {{ $t('message.content.tools.spellcheck.spellingError.close') }}
        </v-btn>
        <v-btn flat color="secondary" @click.prevent="dictionary">
          {{ $t('message.content.tools.spellcheck.spellingError.addToDictionary') }}
        </v-btn>
        <v-btn flat color="secondary" @click.prevent="replace(manualReplacement)">
          {{ checkTranslation }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import SpellingCheckMixin from '@/components/texteditor/mixins/spelling-check.mixin';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'nx-quill-spelling-error',
  mixins: [SpellingCheckMixin],
  props: {
    quill: {
      type: Object,
    },
    error: {
      type: Object,
      required: true,
    },
    heightOffset: {
      type: Number,
      default: 42,
    },
  },
  data: () => ({
    manualReplacement: null,
    inputValue: null,
  }),
  computed: {
    ...mapGetters('staff', { personalDictionary: 'personalDictionary', manualError: 'manualError' }),
    checkTranslation() {
      if (this.manualReplacement !== this.errorWord) {
        return this.$t('message.content.tools.spellcheck.spellingError.replace');
      }

      return this.$t('message.content.tools.spellcheck.spellingError.dismiss');
    },
    errorWord() {
      return this.error.context.text
        .substring(this.error.context.offset, this.error.context.offset + this.error.context.length);
    },
    show() {
      return this.error.uuid === this.manualError;
    },
    color() {
      return this.colorFromSpellingErrorType([this.error]);
    },
    computedStyle() {
      const {
        top, left, height, width,
      } = this.error.position;
      return {
        top: `${Math.round(top) + this.heightOffset}px`,
        left: `${Math.round(left)}px`,
        height: `${Math.round(height)}px`,
        width: `${Math.round(width)}px`,
        'background-color': this.color,
        opacity: 0.2,
      };
    },
  },
  methods: {
    ...mapActions(
      'staff',
      {
        setManualError: 'SET_MANUAL_ERROR',
      },
    ),
    xPosition() {
      const { x } = this.quill.root.getBoundingClientRect();
      const { left } = this.error.position;

      return x + left;
    },
    yPosition() {
      const { y } = this.quill.root.getBoundingClientRect();
      const { top, height } = this.error.position;
      return y + top + height;
    },
    close() {
      this.manualReplacement = this.errorWord;
      this.setManualError(null);
    },
    showClick() {
      // Necesitamos poner a null el valor en la store y en el siguiente tick setearlo al bueno
      // Si lo hacemos directamente no se muestra el menú porque vue no se actualiza
      this.setManualError(null);
      this.$nextTick(() => {
        this.setManualError(this.error.uuid);
      });
    },
    replace(replacement, fromChip) {
      if (!fromChip && this.manualReplacement === this.errorWord) {
        this.$emit('ignore', { word: this.errorWord, error: this.error });
      } else {
        this.$emit('replace', { word: replacement, error: this.error });
      }
      this.close();
    },
    dictionary() {
      this.$emit('dictionary', { words: [...(this.personalDictionary || []), this.errorWord], error: this.error });
      this.close();
    },
  },
  mounted() {
    this.manualReplacement = this.errorWord;
    // Esto es horrosoro, pero para añadir un border-radius como piden desde diseño no queda otra
    // El elemento es dinámico, lo pinta vue al vuelo, y no podemos simplemente añadirle una clase
    // porque al abrir y cerrar el menú, vuetify las pisa todas, con lo que se pierde
    this.$refs.menu.$refs.content.style = `border-radius: 12px; ${this.$refs.menu.$refs.content.style}`;
  },
  watch: {
    show: {
      // This will let Vue know to look inside the object
      deep: true,
      // We have to move our method to a handler field
      handler() {
        if (this.error.uuid === this.manualError) {
          this.$nextTick(() => this.$refs.textInput.focus());
        }
      },
    },
  },
};
</script>

<style lang="stylus">
.quill-spelling-error-card {
  padding: 0 16px;
}

.v-btn.suggestions-button {
  margin-left: 0;
}

.quill-spelling-error-replacement {
  cursor: pointer;
}
.quill-spelling-error {
  position: absolute;
  cursor: pointer;
  &:hover {
    opacity: .8 !important;
  }
}
</style>
